/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Dimensions,
  Linking,
  Share,
  Alert,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  MaterialCommunityIcons,
  Ionicons,
  FontAwesome5,
  FontAwesome,
  Entypo,
  Feather,
  MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as Clipboard from 'expo-clipboard';

const { width, height } = Dimensions.get('window');

const ScholarshipTips = () => {
  const navigation = useNavigation();
  const [darkMode, setDarkMode] = useState(false);
  const [bookmarkedTips, setBookmarkedTips] = useState([]);
  const [completedChecklist, setCompletedChecklist] = useState([]);

  const tipsCategories = [
    {
      id: 'application',
      title: '📝 Application Process',
      icon: 'file-document',
      color: '#667eea',
    },
    {
      id: 'documents',
      title: '📄 Required Documents',
      icon: 'folder',
      color: '#f59e0b',
    },
    {
      id: 'essay',
      title: '✍️ Essay Writing',
      icon: 'pen',
      color: '#10b981',
    },
    {
      id: 'interview',
      title: '🎤 Interview Tips',
      icon: 'microphone',
      color: '#ef4444',
    },
    {
      id: 'timeline',
      title: '⏰ Timeline Planning',
      icon: 'calendar-clock',
      color: '#8b5cf6',
    },
    {
      id: 'resources',
      title: '🔗 Useful Resources',
      icon: 'link',
      color: '#3b82f6',
    },
  ];

  const applicationChecklist = [
    { id: '1', task: 'Research suitable scholarships', completed: false },
    { id: '2', task: 'Check eligibility criteria', completed: false },
    { id: '3', task: 'Prepare academic transcripts', completed: false },
    { id: '4', task: 'Request letters of recommendation', completed: false },
    { id: '5', task: 'Write personal statement/essay', completed: false },
    { id: '6', task: 'Update CV/Resume', completed: false },
    { id: '7', task: 'Gather financial documents', completed: false },
    { id: '8', task: 'Prepare for interview', completed: false },
    { id: '9', task: 'Submit applications before deadline', completed: false },
    { id: '10', task: 'Follow up on applications', completed: false },
  ];

  const tipsContent = {
    application: [
      {
        id: 'app1',
        title: 'Start Early',
        content: 'Begin your scholarship search and application process at least 6-12 months before the deadlines. This gives you ample time to prepare strong applications.',
        icon: 'clock-fast',
      },
      {
        id: 'app2',
        title: 'Read Instructions Carefully',
        content: 'Each scholarship has specific requirements. Read the guidelines thoroughly and follow them exactly. Missing a small detail can disqualify your application.',
        icon: 'file-check',
      },
      {
        id: 'app3',
        title: 'Apply Broadly',
        content: 'Apply to multiple scholarships, including smaller ones. Smaller scholarships often have less competition but can add up significantly.',
        icon: 'target-multiple',
      },
      {
        id: 'app4',
        title: 'Tailor Each Application',
        content: 'Never use a generic application. Customize your essays and materials for each scholarship to show genuine interest and fit.',
        icon: 'account-edit',
      },
      {
        id: 'app5',
        title: 'Proofread Meticulously',
        content: 'Typos and grammatical errors create a poor impression. Have someone else review your application before submission.',
        icon: 'spellcheck',
      },
    ],
    documents: [
      {
        id: 'doc1',
        title: 'Academic Transcripts',
        content: 'Get official transcripts early. Some universities take weeks to process transcript requests.',
        icon: 'certificate',
      },
      {
        id: 'doc2',
        title: 'Letters of Recommendation',
        content: 'Request recommendations 4-6 weeks in advance. Provide recommenders with your CV, personal statement, and scholarship details.',
        icon: 'account-star',
      },
      {
        id: 'doc3',
        title: 'Personal Statement',
        content: 'Keep it focused, authentic, and compelling. Show, don\'t just tell, your achievements and aspirations.',
        icon: 'text-box',
      },
      {
        id: 'doc4',
        title: 'CV/Resume',
        content: 'Format it professionally. Include academic achievements, work experience, extracurricular activities, and skills.',
        icon: 'card-account-details',
      },
      {
        id: 'doc5',
        title: 'Proof of Finances',
        content: 'Some scholarships require evidence of financial need. Prepare bank statements and income proof documents.',
        icon: 'bank',
      },
    ],
    essay: [
      {
        id: 'ess1',
        title: 'Know Your Audience',
        content: 'Research the scholarship provider. Understand their values and tailor your essay to resonate with their mission.',
        icon: 'account-group',
      },
      {
        id: 'ess2',
        title: 'Tell Your Story',
        content: 'Use specific examples and personal anecdotes. Make your essay memorable by showing your unique journey.',
        icon: 'book-open-variant',
      },
      {
        id: 'ess3',
        title: 'Address the Prompt',
        content: 'Stay focused on the question asked. Every paragraph should relate back to the central theme.',
        icon: 'message-question',
      },
      {
        id: 'ess4',
        title: 'Show, Don\'t Tell',
        content: 'Instead of saying "I\'m a leader," describe a situation where you demonstrated leadership.',
      },
      {
        id: 'ess5',
        title: 'Strong Opening & Closing',
        content: 'Start with an engaging hook and end with a powerful conclusion that reinforces your main points.',
        icon: 'format-quote-open',
      },
    ],
    interview: [
      {
        id: 'int1',
        title: 'Research the Institution',
        content: 'Know the university/program you\'re applying for. Be prepared to explain why it\'s the right fit for you.',
        icon: 'school',
      },
      {
        id: 'int2',
        title: 'Practice Common Questions',
        content: 'Prepare for questions about your goals, strengths, weaknesses, and why you deserve the scholarship.',
        icon: 'microphone',
      },
      {
        id: 'int3',
        title: 'Prepare Questions to Ask',
        content: 'Have thoughtful questions ready for the interviewers. This shows genuine interest and engagement.',
        icon: 'help-circle',
      },
      {
        id: 'int4',
        title: 'Dress Professionally',
        content: 'First impressions matter. Dress appropriately for the type of interview (in-person or virtual).',
        icon: 'tshirt-crew',
      },
      {
        id: 'int5',
        title: 'Follow Up',
        content: 'Send a thank-you email within 24 hours, reiterating your interest and key qualifications.',
        icon: 'email',
      },
    ],
    timeline: [
      {
        id: 'time1',
        title: '6-12 Months Before',
        content: 'Start research, identify target scholarships, begin essay brainstorming.',
        icon: 'calendar-month',
      },
      {
        id: 'time2',
        title: '4-6 Months Before',
        content: 'Request transcripts, contact recommenders, draft essays.',
        icon: 'calendar-range',
      },
      {
        id: 'time3',
        title: '2-3 Months Before',
        content: 'Finalize essays, submit applications with buffer time.',
        icon: 'calendar-check',
      },
      {
        id: 'time4',
        title: '1 Month Before',
        content: 'Prepare for interviews, gather interview materials.',
        icon: 'calendar-alert',
      },
      {
        id: 'time5',
        title: 'After Submission',
        content: 'Track applications, prepare for potential interviews.',
        icon: 'calendar-search',
      },
    ],
    resources: [
      {
        id: 'res1',
        title: 'British Council Scholarships',
        link: 'https://www.britishcouncil.org/study-work-abroad/in-uk/scholarships',
        icon: 'earth',
      },
      {
        id: 'res2',
        title: 'UCAS Scholarship Guide',
        link: 'https://www.ucas.com/finance/scholarships-and-bursaries',
        icon: 'school',
      },
      {
        id: 'res3',
        title: 'UK Government Scholarships',
        link: 'https://www.gov.uk/browse/education/student-finance',
        icon: 'government',
      },
      {
        id: 'res4',
        title: 'Scholarship Essay Samples',
        link: 'https://www.prospects.ac.uk/funding-and-support/scholarships',
        icon: 'file-document',
      },
      {
        id: 'res5',
        title: 'CV Builder for Students',
        link: 'https://www.myperfectcv.co.uk/cv-builder/student-cv',
        icon: 'card-bulleted',
      },
    ],
  };

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    orange: ['#dd6b20', '#f6ad55'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
    teal: ['#319795', '#81e6d9'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    teal: ['#667eea', '#764ba2'],
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [darkModePref, bookmarks, checklist] = await Promise.all([
        AsyncStorage.getItem('darkMode'),
        AsyncStorage.getItem('bookmarkedTips'),
        AsyncStorage.getItem('completedChecklist'),
      ]);
      
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
      if (bookmarks) {
        setBookmarkedTips(JSON.parse(bookmarks));
      }
      
      if (checklist) {
        setCompletedChecklist(JSON.parse(checklist));
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const toggleBookmark = async (tipId) => {
    try {
      let newBookmarks;
      if (bookmarkedTips.includes(tipId)) {
        newBookmarks = bookmarkedTips.filter(id => id !== tipId);
      } else {
        newBookmarks = [...bookmarkedTips, tipId];
      }
      
      setBookmarkedTips(newBookmarks);
      await AsyncStorage.setItem('bookmarkedTips', JSON.stringify(newBookmarks));
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const toggleChecklistItem = async (itemId) => {
    try {
      let newChecklist;
      if (completedChecklist.includes(itemId)) {
        newChecklist = completedChecklist.filter(id => id !== itemId);
      } else {
        newChecklist = [...completedChecklist, itemId];
      }
      
      setCompletedChecklist(newChecklist);
      await AsyncStorage.setItem('completedChecklist', JSON.stringify(newChecklist));
    } catch (error) {
      console.error('Error toggling checklist item:', error);
    }
  };

  const openResourceLink = async (link) => {
    try {
      await Linking.openURL(link);
    } catch (error) {
      console.error('Error opening link:', error);
      Alert.alert('Error', 'Failed to open link');
    }
  };

  const copyToClipboard = async (text) => {
    await Clipboard.setStringAsync(text);
    Alert.alert('Copied!', 'Text copied to clipboard');
  };

  const shareTip = async (tip) => {
    try {
      await Share.share({
        title: `Scholarship Tip: ${tip.title}`,
        message: `${tip.title}\n\n${tip.content}\n\nShared from Scholarship App`,
      });
    } catch (error) {
      console.error('Error sharing tip:', error);
    }
  };

  const renderHeader = () => (
    <LinearGradient
      colors={gradients.primary}
      style={styles.header}
    >
      <View style={styles.headerTop}>
        <TouchableOpacity 
          style={styles.backButton} 
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>💡 Scholarship Tips</Text>
        <TouchableOpacity 
          style={styles.statsButton}
          onPress={() => navigation.navigate('ScholarshipStats')}
        >
          <MaterialCommunityIcons name="chart-box" size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>
      
      <Text style={styles.headerSubtitle}>
        Expert guidance to help you secure funding for your UK engineering education
      </Text>
    </LinearGradient>
  );

  const renderProgressCard = () => {
    const completedCount = completedChecklist.length;
    const totalCount = applicationChecklist.length;
    const progressPercentage = (completedCount / totalCount) * 100;

    return (
      <View style={styles.progressCard}>
        <LinearGradient
          colors={gradients.success}
          style={styles.progressGradient}
        >
          <View style={styles.progressHeader}>
            <View>
              <Text style={styles.progressTitle}>Application Checklist</Text>
              <Text style={styles.progressSubtitle}>
                Track your application progress
              </Text>
            </View>
            <View style={styles.progressCircle}>
              <Text style={styles.progressPercentage}>
                {Math.round(progressPercentage)}%
              </Text>
            </View>
          </View>
          
          <View style={styles.progressBarContainer}>
            <View style={styles.progressBar}>
              <View 
                style={[
                  styles.progressFill,
                  { width: `${progressPercentage}%` }
                ]} 
              />
            </View>
            <Text style={styles.progressText}>
              {completedCount} of {totalCount} tasks completed
            </Text>
          </View>
          
          <TouchableOpacity 
            style={styles.viewChecklistButton}
            onPress={() => renderChecklistModal()}
          >
            <Text style={styles.viewChecklistText}>View Full Checklist</Text>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#ffffff" />
          </TouchableOpacity>
        </LinearGradient>
      </View>
    );
  };

  const renderChecklistModal = () => {
    Alert.alert(
      'Application Checklist',
      'Mark completed tasks as you progress through your applications',
      [
        {
          text: 'Close',
          style: 'cancel',
        },
        {
          text: 'Clear All',
          onPress: () => {
            setCompletedChecklist([]);
            AsyncStorage.removeItem('completedChecklist');
          },
          style: 'destructive',
        },
      ],
      { cancelable: true }
    );
  };

  const renderCategoryCard = (category) => (
    <TouchableOpacity
      key={category.id}
      style={styles.categoryCard}
      onPress={() => renderCategoryTips(category)}
    >
      <LinearGradient
        colors={[category.color, `${category.color}80`]}
        style={styles.categoryGradient}
      >
        <View style={styles.categoryIcon}>
          <MaterialCommunityIcons name={category.icon} size={32} color="#ffffff" />
        </View>
        <Text style={styles.categoryTitle}>{category.title}</Text>
        <Text style={styles.categoryCount}>
          {tipsContent[category.id]?.length || 0} tips
        </Text>
      </LinearGradient>
    </TouchableOpacity>
  );

  const renderCategoryTips = (category) => {
    const tips = tipsContent[category.id] || [];
    
    Alert.alert(
      category.title,
      'Select a tip to learn more:',
      tips.map(tip => ({
        text: tip.title,
        onPress: () => renderTipDetail(tip, category),
      })),
      { cancelable: true }
    );
  };

  const renderTipDetail = (tip, category) => {
    const isBookmarked = bookmarkedTips.includes(tip.id);
    
    Alert.alert(
      tip.title,
      tip.content,
      [
        {
          text: isBookmarked ? 'Remove Bookmark' : 'Bookmark',
          onPress: () => toggleBookmark(tip.id),
        },
        tip.link ? {
          text: 'Open Link',
          onPress: () => openResourceLink(tip.link),
        } : null,
        {
          text: 'Share',
          onPress: () => shareTip(tip),
        },
        {
          text: 'Copy Text',
          onPress: () => copyToClipboard(`${tip.title}\n\n${tip.content}`),
        },
        {
          text: 'Close',
          style: 'cancel',
        },
      ].filter(Boolean),
      { cancelable: true }
    );
  };

  const renderBookmarkedTips = () => {
    if (bookmarkedTips.length === 0) return null;

    const bookmarkedItems = [];
    Object.keys(tipsContent).forEach(categoryId => {
      tipsContent[categoryId].forEach(tip => {
        if (bookmarkedTips.includes(tip.id)) {
          bookmarkedItems.push({ ...tip, categoryId });
        }
      });
    });

    return (
      <View style={styles.bookmarksSection}>
        <View style={styles.sectionHeader}>
          <MaterialCommunityIcons name="bookmark" size={20} color="#667eea" />
          <Text style={styles.sectionTitle}>Bookmarked Tips</Text>
          <TouchableOpacity onPress={() => setBookmarkedTips([])}>
            <Text style={styles.clearText}>Clear All</Text>
          </TouchableOpacity>
        </View>
        
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {bookmarkedItems.map((tip) => (
            <TouchableOpacity
              key={tip.id}
              style={styles.bookmarkCard}
              onPress={() => renderTipDetail(tip, { id: tip.categoryId })}
            >
              <View style={styles.bookmarkHeader}>
                <MaterialCommunityIcons name={tip.icon} size={16} color="#667eea" />
                <TouchableOpacity
                  style={styles.removeBookmarkButton}
                  onPress={() => toggleBookmark(tip.id)}
                >
                  <MaterialCommunityIcons name="close" size={12} color="#ef4444" />
                </TouchableOpacity>
              </View>
              <Text style={styles.bookmarkTitle} numberOfLines={2}>{tip.title}</Text>
              <Text style={styles.bookmarkContent} numberOfLines={3}>{tip.content}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderQuickTips = () => (
    <View style={styles.quickTipsSection}>
      <View style={styles.sectionHeader}>
        <MaterialCommunityIcons name="lightning-bolt" size={20} color="#f59e0b" />
        <Text style={styles.sectionTitle}>Quick Success Tips</Text>
      </View>
      
      <View style={styles.quickTipsGrid}>
        <View style={styles.quickTipCard}>
          <LinearGradient
            colors={gradients.blue}
            style={styles.quickTipGradient}
          >
            <MaterialCommunityIcons name="calendar-check" size={24} color="#ffffff" />
            <Text style={styles.quickTipTitle}>Deadlines First</Text>
            <Text style={styles.quickTipText}>
              Prioritize applications with earliest deadlines
            </Text>
          </LinearGradient>
        </View>
        
        <View style={styles.quickTipCard}>
          <LinearGradient
            colors={gradients.green}
            style={styles.quickTipGradient}
          >
            <MaterialCommunityIcons name="account-multiple" size={24} color="#ffffff" />
            <Text style={styles.quickTipTitle}>Network</Text>
            <Text style={styles.quickTipText}>
              Connect with current scholars and alumni
            </Text>
          </LinearGradient>
        </View>
        
        <View style={styles.quickTipCard}>
          <LinearGradient
            colors={gradients.purple}
            style={styles.quickTipGradient}
          >
            <MaterialCommunityIcons name="update" size={24} color="#ffffff" />
            <Text style={styles.quickTipTitle}>Update Regularly</Text>
            <Text style={styles.quickTipText}>
              Keep improving your application materials
            </Text>
          </LinearGradient>
        </View>
        
        <View style={styles.quickTipCard}>
          <LinearGradient
            colors={gradients.orange}
            style={styles.quickTipGradient}
          >
            <MaterialCommunityIcons name="message-alert" size={24} color="#ffffff" />
            <Text style={styles.quickTipTitle}>Follow Up</Text>
            <Text style={styles.quickTipText}>
              Don't hesitate to ask for application status
            </Text>
          </LinearGradient>
        </View>
      </View>
    </View>
  );

  const renderEmergencyContacts = () => (
    <View style={styles.contactsSection}>
      <View style={styles.sectionHeader}>
        <MaterialCommunityIcons name="phone" size={20} color="#ef4444" />
        <Text style={styles.sectionTitle}>Emergency Contacts</Text>
      </View>
      
      <View style={styles.contactsGrid}>
        <TouchableOpacity 
          style={styles.contactCard}
          onPress={() => openResourceLink('tel:08001009090')}
        >
          <MaterialCommunityIcons name="school" size={24} color="#667eea" />
          <Text style={styles.contactTitle}>University Support</Text>
          <Text style={styles.contactNumber}>0800 100 9090</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.contactCard}
          onPress={() => openResourceLink('tel:08082399239')}
        >
          <MaterialCommunityIcons name="heart" size={24} color="#ef4444" />
          <Text style={styles.contactTitle}>Student Helpline</Text>
          <Text style={styles.contactNumber}>0808 239 9239</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.contactCard}
          onPress={() => openResourceLink('tel:08001009022')}
        >
          <MaterialCommunityIcons name="shield-account" size={24} color="#10b981" />
          <Text style={styles.contactTitle}>International Support</Text>
          <Text style={styles.contactNumber}>0800 100 9022</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.contactCard}
          onPress={() => openResourceLink('tel:116123')}
        >
          <MaterialCommunityIcons name="lifebuoy" size={24} color="#f59e0b" />
          <Text style={styles.contactTitle}>Samaritans</Text>
          <Text style={styles.contactNumber}>116 123</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const styles = getStyles(darkMode);

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderHeader()}
      
      <ScrollView 
        style={styles.container}
        showsVerticalScrollIndicator={false}
      >
        {renderProgressCard()}
        
        <View style={styles.sectionHeader}>
          <MaterialCommunityIcons name="shape" size={20} color="#667eea" />
          <Text style={styles.sectionTitle}>Tips by Category</Text>
        </View>
        
        <View style={styles.categoriesGrid}>
          {tipsCategories.map(category => renderCategoryCard(category))}
        </View>
        
        {renderBookmarkedTips()}
        {renderQuickTips()}
        {renderEmergencyContacts()}
        
        <View style={styles.bottomSection}>
          <Text style={styles.bottomTitle}>Need More Help?</Text>
          <Text style={styles.bottomText}>
            Visit our resources section for templates, samples, and expert advice
          </Text>
          
          <TouchableOpacity 
            style={styles.resourcesButton}
            onPress={() => renderCategoryTips(tipsCategories[5])}
          >
            <MaterialCommunityIcons name="library" size={20} color="#ffffff" />
            <Text style={styles.resourcesButtonText}>View All Resources</Text>
          </TouchableOpacity>
        </View>
        
        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 30,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    flex: 1,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  statsButton: {
    padding: 8,
  },
  headerSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
    textAlign: 'center',
    lineHeight: 24,
  },
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  progressCard: {
    marginTop: -20,
    marginBottom: 24,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  progressGradient: {
    padding: 20,
    borderRadius: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  progressTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  progressSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  progressCircle: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressPercentage: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  progressBarContainer: {
    marginBottom: 16,
  },
  progressBar: {
    height: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 4,
    marginBottom: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  viewChecklistButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
  },
  viewChecklistText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 4,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginLeft: 8,
    flex: 1,
  },
  clearText: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '600',
  },
  categoriesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  categoryCard: {
    width: (width - 52) / 2,
    height: 140,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  categoryGradient: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    justifyContent: 'space-between',
  },
  categoryIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  categoryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  categoryCount: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  bookmarksSection: {
    marginBottom: 24,
  },
  bookmarkCard: {
    width: 200,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginRight: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  bookmarkHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  removeBookmarkButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bookmarkTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  bookmarkContent: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    lineHeight: 18,
  },
  quickTipsSection: {
    marginBottom: 24,
  },
  quickTipsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  quickTipCard: {
    width: (width - 52) / 2,
    height: 140,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  quickTipGradient: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    justifyContent: 'space-between',
  },
  quickTipTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 8,
    marginBottom: 4,
  },
  quickTipText: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 16,
  },
  contactsSection: {
    marginBottom: 24,
  },
  contactsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  contactCard: {
    width: (width - 52) / 2,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  contactTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 12,
    marginBottom: 4,
    textAlign: 'center',
  },
  contactNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#667eea',
    textAlign: 'center',
  },
  bottomSection: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 20,
    borderRadius: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  bottomTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  bottomText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  resourcesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  resourcesButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  bottomPadding: {
    height: 100,
  },
});

export default ScholarshipTips;